import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Две колонки с синхронной прокруткой.
 *
 * ── Прокрутка синхронизируется пропорцией, а не пикселями ──────────
 *
 * Соблазн — приравнять scrollTop одной панели к scrollTop другой.
 * Это работает ровно до первой пары, где слева шесть фото, а справа
 * три: колонки разной высоты, и к середине левой правая уже упёрлась
 * в конец. Дальше диспетчер крутит одну, а вторая стоит — то есть
 * сравнивать нечего.
 *
 * Поэтому ведущая панель отдаёт долю пройденного пути (0..1), а
 * ведомая переводит её в свои пиксели. Низ совпадает с низом,
 * верх с верхом, при любой разнице высот.
 *
 * ── Ведущая панель на время жеста одна ────────────────────────────
 *
 * Присвоение scrollTop ведомой панели само порождает событие scroll.
 * Без защиты панели начинают толкать друг друга: из-за округления
 * доли обратный пересчёт даёт чуть другое число, и картинка дрожит.
 * Пока идёт жест, ведущей считается та панель, которую тронули
 * первой; события ведомой игнорируются до следующего кадра тишины.
 *
 * ── На узком экране синхронизации нет ─────────────────────────────
 *
 * Колонки складываются одна под другую (это требование брифа: сжатый
 * split на телефоне нечитаем), и внутренней прокрутки у них больше
 * нет — страница прокручивается целиком. Синхронизировать нечего,
 * поэтому обработчики не вешаются вовсе.
 */

const STACK_QUERY = '(max-width: 900px)'

function useStacked() {
  const [stacked, setStacked] = useState(false)

  useEffect(() => {
    // matchMedia нет в jsdom старых версий и в SSR — молча считаем,
    // что экран широкий, вместо падения на старте.
    if (typeof window === 'undefined' || !window.matchMedia) return undefined

    const mq = window.matchMedia(STACK_QUERY)
    const apply = (e) => setStacked(e.matches)
    apply(mq)

    // addListener — для Safari до 14, где addEventListener у MediaQueryList ещё нет.
    if (mq.addEventListener) {
      mq.addEventListener('change', apply)
      return () => mq.removeEventListener('change', apply)
    }
    mq.addListener(apply)
    return () => mq.removeListener(apply)
  }, [])

  return stacked
}

/** Доля пройденной прокрутки, 0..1. Панель без переполнения — всегда 0. */
export function scrollRatio(el) {
  const range = el.scrollHeight - el.clientHeight
  if (range <= 0) return 0
  return Math.min(1, Math.max(0, el.scrollTop / range))
}

/** Применить долю к панели. Обратная операция к scrollRatio. */
export function applyScrollRatio(el, ratio) {
  const range = el.scrollHeight - el.clientHeight
  if (range <= 0) return
  el.scrollTop = ratio * range
}

export function SplitView({
  leftLabel,
  rightLabel,
  left,
  right,
  /** Подписи под заголовками колонок: даты акта, счётчики. */
  leftMeta = null,
  rightMeta = null,
  sync = true,
  /** Доступное имя всей пары колонок. */
  label = 'Сравнение',
  className = '',
}) {
  const leftRef = useRef(null)
  const rightRef = useRef(null)
  const leader = useRef(null)
  const frame = useRef(0)
  const stacked = useStacked()
  const active = sync && !stacked

  const handleScroll = useCallback((from) => {
    if (!active) return
    const source = from === 'left' ? leftRef.current : rightRef.current
    const target = from === 'left' ? rightRef.current : leftRef.current
    if (!source || !target) return

    // Событие пришло от ведомой панели — это эхо нашего же присвоения.
    if (leader.current && leader.current !== from) return
    leader.current = from

    applyScrollRatio(target, scrollRatio(source))

    // Ведущую отпускаем в следующем кадре: к этому моменту эхо-события
    // уже отработали, и следующий жест может начать другая панель.
    cancelAnimationFrame(frame.current)
    frame.current = requestAnimationFrame(() => {
      leader.current = null
    })
  }, [active])

  useEffect(() => () => cancelAnimationFrame(frame.current), [])

  return (
    <div
      className={['split-view', stacked ? 'is-stacked' : '', className]
        .join(' ').trim()}
      role="group"
      aria-label={label}
    >
      <section className="split-view__pane">
        <header className="split-view__head">
          <h3 className="split-view__title">{leftLabel}</h3>
          {leftMeta && <p className="split-view__meta">{leftMeta}</p>}
        </header>
        <div
          className="split-view__body"
          ref={leftRef}
          onScroll={() => handleScroll('left')}
          // Прокручиваемая область должна получать фокус с клавиатуры,
          // иначе колонку не пролистать без мыши.
          tabIndex={active ? 0 : -1}
        >
          {left}
        </div>
      </section>

      <section className="split-view__pane">
        <header className="split-view__head">
          <h3 className="split-view__title">{rightLabel}</h3>
          {rightMeta && <p className="split-view__meta">{rightMeta}</p>}
        </header>
        <div
          className="split-view__body"
          ref={rightRef}
          onScroll={() => handleScroll('right')}
          tabIndex={active ? 0 : -1}
        >
          {right}
        </div>
      </section>
    </div>
  )
}
